import {
  LineupPlayer,
  withHasSecondaryPosition,
} from '@collector/desktop-feature-lineups'
import { SubParticipantPosition } from '@collector/sportsapi-client-legacy'
import { SubParticipant } from '@collector/sportsapi-types'
import { PlayersSecondaryPositionTable } from '@desktop/utils/database'
import { IncidentPlayerOption } from '@desktop/views/Relation/types'
import { isSameSubparticipant } from '@desktop/views/Relation/utils'

export type StoredPlayerRef = { shortName: string; id: number | null }

type SecondaryPositionRecord = {
  players?: StoredPlayerRef[]
  pitcherOrder?: StoredPlayerRef[]
}

function isPossiblePitcherInRecord(
  player: SubParticipant,
  record: SecondaryPositionRecord | undefined,
): boolean {
  if (player.position !== SubParticipantPosition.Batter || !record?.players) {
    return false
  }

  return record.players.some((storedPlayer) =>
    isSameSubparticipant(storedPlayer, {
      id: player.id,
      shortName: player.short_name,
    }),
  )
}

/**
 * Applies IndexedDB possible-pitcher flags and Pitchers-section order.
 * Possible batters come from API `flags`; IndexedDB is only a fallback for
 * possible pitchers (no `possible_pitcher` API flag).
 */
export function applySecondaryPositionRecord<T extends SubParticipant>(
  player: T,
  record: SecondaryPositionRecord | undefined,
): LineupPlayer {
  const isPossiblePitcher = isPossiblePitcherInRecord(player, record)
  const withSecondaryPosition = withHasSecondaryPosition(
    player,
    isPossiblePitcher,
  )

  return withPitcherOrder(withSecondaryPosition, record?.pitcherOrder)
}

export async function transformBaseballSubParticipants(
  eventId: number,
  players: SubParticipant[],
): Promise<LineupPlayer[]> {
  const records = await PlayersSecondaryPositionTable.getAll(eventId)
  const recordsByParticipantId = new Map(
    records.map((record) => [record.participantId, record]),
  )

  return players.map((player) => {
    const record = recordsByParticipantId.get(Number(player.team_id))
    return applySecondaryPositionRecord(player, record)
  })
}

function getStoredPlayerIndex(
  player: { id: number | null; short_name: string },
  order: StoredPlayerRef[],
): number {
  return order.findIndex((stored) =>
    isSameSubparticipant(
      { id: player.id, shortName: player.short_name },
      stored,
    ),
  )
}

function compareByPitcherOrder(
  a: { pitcher_order?: number },
  b: { pitcher_order?: number },
): number {
  return (a.pitcher_order ?? Infinity) - (b.pitcher_order ?? Infinity)
}

/**
 * Stamps client-side `pitcher_order` from the last saved Pitchers-section
 * order. SportsAPI leaves `lineup_order` null for pitchers, so this is the
 * equivalent of batting `lineup_order`.
 */
export function withPitcherOrder<T extends SubParticipant>(
  player: T,
  pitcherOrder: StoredPlayerRef[] | undefined,
): T & { pitcher_order?: number } {
  if (
    player.position !== SubParticipantPosition.Pitcher ||
    !pitcherOrder?.length
  ) {
    return player
  }

  const index = getStoredPlayerIndex(player, pitcherOrder)
  if (index < 0) {
    return player
  }

  return { ...player, pitcher_order: index }
}

export function sortPitcherIncidentOptions(
  options: IncidentPlayerOption[],
): IncidentPlayerOption[] {
  const primary: IncidentPlayerOption[] = []
  const secondary: IncidentPlayerOption[] = []

  for (const option of options) {
    if (option.metadata.position === SubParticipantPosition.Pitcher) {
      primary.push(option)
    } else {
      secondary.push(option)
    }
  }

  primary.sort(
    (a, b) =>
      (a.metadata.pitcherOrder ?? Infinity) -
      (b.metadata.pitcherOrder ?? Infinity),
  )

  return [...primary, ...secondary]
}

export function toSortedByPitcherOrder(
  pitchers: LineupPlayer[],
): LineupPlayer[] {
  return [...pitchers].sort(compareByPitcherOrder)
}
